// Home page component with welcome content and call-to-action

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Home: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="text-center py-16">
        <h1 className="text-5xl font-bold text-gray-900 mb-6">
          English Learning Tools
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Take our comprehensive English placement test to determine your proficiency level. 
          Get detailed feedback and track your progress as you improve your English skills.
        </p>
        
        {user ? (
          <div className="space-x-4">
            <Link
              to="/test"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              Take Placement Test
            </Link>
            <Link
              to="/profile"
              className="inline-block bg-gray-200 text-gray-800 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              View Profile
            </Link>
          </div>
        ) : (
          <div className="space-x-4">
            <Link
              to="/register"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              Get Started
            </Link>
            <Link
              to="/login"
              className="inline-block bg-gray-200 text-gray-800 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Login
            </Link>
          </div>
        )}
      </div>

      {/* Tools Section */}
      <div className="py-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Available Tools</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Placement Test Tool */}
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Placement Test</h3>
            <p className="text-gray-600 mb-4 text-center">
              Determine your English proficiency level with our comprehensive 100-question test.
            </p>
            <div className="text-center">
              <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                Available
              </span>
            </div>
            {user && (
              <div className="mt-4 text-center">
                <Link
                  to="/test"
                  className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Start Test
                </Link>
              </div>
            )}
          </div>

          {/* IELTS Mock Tool */}
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">IELTS Mock Exams</h3>
            <p className="text-gray-600 mb-4 text-center">
              Practice with full-length IELTS mock exams to prepare for the real test.
            </p>
            <div className="text-center">
              <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                Available
              </span>
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/ielts"
                className="inline-block bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                Start IELTS Test
              </Link>
            </div>
          </div>

          {/* Grammar Practice Tool */}
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Grammar Practice</h3>
            <p className="text-gray-600 mb-4 text-center">
              Improve your grammar skills with interactive exercises and detailed explanations.
            </p>
            <div className="text-center">
              <span className="inline-block bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
                Coming Soon
              </span>
            </div>
          </div>

          {/* Vocabulary Builder Tool */}
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Vocabulary Builder</h3>
            <p className="text-gray-600 mb-4 text-center">
              Expand your vocabulary with CEFR-leveled words, flashcards, and spaced repetition learning.
            </p>
            <div className="text-center">
              <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                Available
              </span>
            </div>
            {user && (
              <div className="mt-4 text-center">
                <Link
                  to="/vocabulary"
                  className="inline-block bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
                >
                  Start Learning
                </Link>
              </div>
            )}
          </div>

          {/* Speaking Practice Tool */}
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Speaking Practice</h3>
            <p className="text-gray-600 mb-4 text-center">
              Practice your speaking skills with AI-powered conversation partners and pronunciation feedback.
            </p>
            <div className="text-center">
              <span className="inline-block bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
                Coming Soon
              </span>
            </div>
          </div>

          {/* Writing Assistant Tool */}
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Writing Assistant</h3>
            <p className="text-gray-600 mb-4 text-center">
              Get AI-powered feedback on your writing with grammar checks and style suggestions.
            </p>
            <div className="text-center">
              <span className="inline-block bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
                Coming Soon
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Test Information */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">About the Placement Test</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Test Format</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• 100 multiple-choice questions</li>
              <li>• Covers grammar, vocabulary, and comprehension</li>
              <li>• Takes approximately 45-60 minutes</li>
              <li>• Questions progress from basic to advanced</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Scoring System</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• A1 (Beginner): 0-49%</li>
              <li>• A2 (Elementary): 50-59%</li>
              <li>• B1 (Intermediate): 60-69%</li>
              <li>• B2 (Upper Intermediate): 70-79%</li>
              <li>• C1 (Advanced): 80-89%</li>
              <li>• C2 (Proficient): 90-100%</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
